<template>
  <Teleport to="body">
    <Transition name="scio-modal">
      <div
        v-if="open"
        class="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-slate-950/45 p-3 backdrop-blur-[2px] sm:p-6"
        role="presentation"
        @mousedown.self="handleBackdrop"
      >
        <section
          ref="panel"
          class="relative my-auto flex max-h-[calc(100dvh-1.5rem)] w-full flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl outline-none sm:max-h-[calc(100dvh-3rem)]"
          :class="[sizeClass, panelClass]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? titleId : undefined"
          :aria-label="title ? undefined : ariaLabel"
          :aria-describedby="hasDescription ? descriptionId : undefined"
          tabindex="-1"
          @keydown="handleKeydown"
        >
          <header
            v-if="$slots.header || title || description || showClose"
            class="flex shrink-0 items-start gap-3 border-b border-slate-200 px-5 py-4 sm:px-6"
            :class="headerClass"
          >
            <slot name="header">
              <div
                v-if="$slots.icon"
                class="grid h-10 w-10 shrink-0 place-items-center bg-scio-yellow-p3/20 text-slate-800"
                aria-hidden="true"
              >
                <slot name="icon" />
              </div>

              <div class="min-w-0 flex-1">
                <h2 :id="titleId" class="scio-text-lead scio-weight-semibold text-slate-900">
                  {{ title }}
                </h2>
                <p
                  v-if="description || $slots.description"
                  :id="descriptionId"
                  class="mt-1 max-w-3xl whitespace-pre-line scio-text-body leading-6 text-slate-600"
                >
                  <slot name="description">{{ description }}</slot>
                </p>
              </div>
            </slot>

            <button
              v-if="showClose"
              type="button"
              class="scio-modal-close"
              :disabled="busy"
              aria-label="Close dialog"
              title="Close"
              @click="requestClose"
            >
              <img src="/icons/scio-cancel.svg" alt="" aria-hidden="true" />
            </button>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6" :class="bodyClass">
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            class="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6"
            :class="footerClass"
          >
            <slot name="footer" />
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

let modalSequence = 0;
let openModalCount = 0;

const props = defineProps({
  open: { type: Boolean, default: true },
  title: { type: String, default: "" },
  ariaLabel: { type: String, default: "Dialog" },
  description: { type: String, default: "" },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["sm", "md", "lg", "xl", "full"].includes(value),
  },
  showClose: { type: Boolean, default: true },
  closeOnBackdrop: { type: Boolean, default: true },
  closeOnEscape: { type: Boolean, default: true },
  busy: { type: Boolean, default: false },
  initialFocus: { type: String, default: "" },
  panelClass: { type: [String, Array, Object], default: "" },
  headerClass: { type: [String, Array, Object], default: "" },
  bodyClass: { type: [String, Array, Object], default: "" },
  footerClass: { type: [String, Array, Object], default: "" },
});

const emit = defineEmits(["close", "opened"]);
const panel = ref(null);
const instanceId = ++modalSequence;
const titleId = `scio-modal-title-${instanceId}`;
const descriptionId = `scio-modal-description-${instanceId}`;
let previouslyFocused = null;
let ownsBodyLock = false;

const sizeClass = computed(() => ({
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-6xl",
  full: "max-w-[calc(100vw-1.5rem)] sm:max-w-[calc(100vw-3rem)]",
}[props.size]));

const hasDescription = computed(() => Boolean(props.description));

function focusableElements() {
  if (!panel.value) return [];
  return Array.from(panel.value.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true");
}

function focusInitialElement() {
  const requested = props.initialFocus ? panel.value?.querySelector(props.initialFocus) : null;
  const target = requested || focusableElements()[0] || panel.value;
  target?.focus?.({ preventScroll: true });
}

function requestClose() {
  if (!props.busy) emit("close");
}

function handleBackdrop() {
  if (props.closeOnBackdrop) requestClose();
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.closeOnEscape) {
    event.preventDefault();
    requestClose();
    return;
  }

  if (event.key !== "Tab") return;
  const elements = focusableElements();
  if (!elements.length) {
    event.preventDefault();
    panel.value?.focus?.();
    return;
  }

  const first = elements[0];
  const last = elements[elements.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function lockBody() {
  if (ownsBodyLock) return;
  ownsBodyLock = true;
  openModalCount += 1;
  if (openModalCount === 1) document.body.classList.add("overflow-hidden");
}

function unlockBody() {
  if (!ownsBodyLock) return;
  ownsBodyLock = false;
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount === 0) document.body.classList.remove("overflow-hidden");
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      previouslyFocused = document.activeElement;
      lockBody();
      await nextTick();
      focusInitialElement();
      emit("opened");
    } else {
      unlockBody();
      previouslyFocused?.focus?.({ preventScroll: true });
      previouslyFocused = null;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  unlockBody();
  previouslyFocused?.focus?.({ preventScroll: true });
});
</script>

<style scoped>
.scio-modal-enter-active,
.scio-modal-leave-active {
  transition: opacity 160ms ease;
}

.scio-modal-close {
  display: inline-grid;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 2.5rem;
  place-items: center;
  border: 1px solid transparent;
  border-radius: 0.625rem;
  color: rgb(100 116 139);
  transition: background-color 160ms ease, border-color 160ms ease;
}

.scio-modal-close:hover:not(:disabled) {
  border-color: rgb(226 232 240);
  background: rgb(248 250 252);
}

.scio-modal-close:focus-visible {
  outline: 3px solid rgba(242, 200, 75, 0.36);
  outline-offset: 2px;
}

.scio-modal-close:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.scio-modal-close img {
  width: 1rem;
  height: 1rem;
}

.scio-modal-enter-active section,
.scio-modal-leave-active section {
  transition: opacity 160ms ease, transform 160ms ease;
}

.scio-modal-enter-from,
.scio-modal-leave-to,
.scio-modal-enter-from section,
.scio-modal-leave-to section {
  opacity: 0;
}

.scio-modal-enter-from section,
.scio-modal-leave-to section {
  transform: translateY(8px) scale(0.99);
}

@media (prefers-reduced-motion: reduce) {
  .scio-modal-enter-active,
  .scio-modal-leave-active,
  .scio-modal-enter-active section,
  .scio-modal-leave-active section {
    transition: none;
  }
}
</style>
